/**
 * The desktop frame's navigation, modelled on the ManageEngine MDM console the
 * owner chose (2026-09-26): a dark MODULE bar across the top, a collapsible
 * SIDEBAR of groups for the active module, and a BREADCRUMB.
 *
 * Pure data + pure lookups — no React — so the mapping of every AIM page to its
 * module, group and crumb is tested on its own (`tests/desktop-modules.test.ts`).
 *
 * Visibility follows the same rules as before: an item with a `pageKey` shows
 * only when that page is in the user's visible set; `adminOnly` items and the
 * Admin module show only to admins. Routes and permissions are untouched — this
 * decides what is DRAWN, never what is allowed.
 */
import type { PageKey } from "@/lib/rbac/pages";

export interface ModuleItem {
  label: string;
  href: string;
  pageKey?: PageKey;
  adminOnly?: boolean;
  /** Extra path prefixes that light this item (sub-pages, detail pages). */
  alsoMatches?: string[];
  /** Match only the exact path (e.g. a module landing page). */
  exact?: boolean;
}

export interface ModuleGroup {
  title: string;
  items: ModuleItem[];
}

export type ModuleKey =
  | "home"
  | "account"
  | "loans"
  | "itr"
  | "rent"
  | "invest"
  | "reports"
  | "admin";

export interface ModuleDef {
  key: ModuleKey;
  label: string;
  href: string;
  /** Path prefixes owned by this module. */
  prefixes: string[];
  pageKey?: PageKey;
  adminOnly?: boolean;
  /** The Account module carries the "+ New Record" button in its sidebar. */
  newRecord?: boolean;
  groups: ModuleGroup[];
}

export const MODULES: ModuleDef[] = [
  {
    key: "home",
    label: "Home",
    href: "/dashboard",
    prefixes: ["/dashboard"],
    pageKey: "dashboard",
    groups: [
      {
        title: "Overview",
        items: [
          { label: "Dashboard", href: "/dashboard", pageKey: "dashboard" },
          { label: "Statistics", href: "/analytics", pageKey: "analytics" },
          { label: "Notifications", href: "/notifications" },
        ],
      },
    ],
  },
  {
    key: "account",
    label: "Account",
    href: "/accounts",
    prefixes: ["/accounts", "/records", "/income", "/expenses", "/budget", "/templates", "/vault", "/groups"],
    pageKey: "accounts",
    newRecord: true,
    groups: [
      {
        title: "Manage",
        items: [
          { label: "Accounts", href: "/accounts", pageKey: "accounts" },
          {
            label: "Records",
            href: "/records",
            pageKey: "records",
            alsoMatches: ["/income", "/expenses"],
          },
          { label: "Budgets", href: "/budget", pageKey: "budget", exact: true },
          { label: "Planned", href: "/budget?tab=planned", pageKey: "budget" },
        ],
      },
      {
        title: "Entries",
        items: [
          { label: "Templates", href: "/templates", pageKey: "templates" },
          { label: "Rules", href: "/records/rules", pageKey: "records" },
          { label: "Bank Statements", href: "/records/statements", pageKey: "records" },
        ],
      },
      {
        title: "Library",
        items: [
          { label: "Vault", href: "/vault", pageKey: "vault" },
          { label: "Groups & Splits", href: "/groups" },
        ],
      },
    ],
  },
  {
    key: "loans",
    label: "Loans",
    href: "/loans",
    prefixes: ["/loans", "/home-loan"],
    pageKey: "loans",
    groups: [
      {
        title: "Loans",
        items: [
          { label: "Loan Tracker", href: "/loans", pageKey: "loans" },
          { label: "Home Loan", href: "/home-loan", pageKey: "home-loan", adminOnly: true },
        ],
      },
    ],
  },
  {
    key: "itr",
    label: "ITR",
    href: "/itr",
    prefixes: ["/itr", "/yearly-summary"],
    pageKey: "itr",
    groups: [
      {
        title: "Tax",
        items: [
          { label: "ITR Records", href: "/itr", pageKey: "itr" },
          { label: "Yearly Summary", href: "/yearly-summary", pageKey: "yearly-summary" },
        ],
      },
    ],
  },
  {
    key: "rent",
    label: "Rent",
    href: "/rent",
    prefixes: ["/rent"],
    pageKey: "rent",
    groups: [
      { title: "Overview", items: [{ label: "Dashboard", href: "/rent", exact: true }] },
      {
        title: "Portfolio",
        items: [
          { label: "Properties", href: "/rent/properties" },
          { label: "Tenants", href: "/rent/tenants" },
          { label: "Vacancy", href: "/rent/vacancy" },
          { label: "Brokers", href: "/rent/brokers" },
          { label: "Agreements", href: "/rent/agreements" },
        ],
      },
      {
        title: "Money",
        items: [
          { label: "Payments", href: "/rent/payments" },
          { label: "Maintenance", href: "/rent/maintenance" },
          { label: "Expenses", href: "/rent/expenses" },
          { label: "Statements", href: "/rent/statements" },
        ],
      },
      {
        title: "Compliance & Reports",
        items: [
          { label: "Analytics", href: "/rent/reports" },
          { label: "Tax & Compliance", href: "/rent/tds" },
          { label: "Reminders", href: "/rent/reminders/log" },
        ],
      },
      {
        title: "Settings",
        items: [
          { label: "Settings", href: "/rent/settings/reminders", alsoMatches: ["/rent/settings"] },
          { label: "Profile", href: "/rent/landlord" },
        ],
      },
    ],
  },
  {
    key: "invest",
    label: "Invest",
    href: "/invest",
    prefixes: ["/invest"],
    pageKey: "invest",
    groups: [{ title: "Wealth", items: [{ label: "Portfolio", href: "/invest", pageKey: "invest" }] }],
  },
  {
    key: "reports",
    label: "Reports",
    href: "/reports",
    prefixes: ["/reports", "/analytics", "/notifications"],
    groups: [
      {
        title: "Reports",
        items: [
          { label: "Statistics", href: "/analytics", pageKey: "analytics" },
          { label: "Reports", href: "/reports" },
          { label: "Notifications", href: "/notifications" },
        ],
      },
    ],
  },
  {
    key: "admin",
    label: "Admin",
    href: "/admin",
    prefixes: ["/admin", "/settings", "/preferences", "/security"],
    adminOnly: true,
    groups: [
      {
        title: "Console",
        items: [
          { label: "Overview", href: "/admin", exact: true },
          { label: "People", href: "/admin/people" },
          { label: "Modules", href: "/admin/modules", alsoMatches: ["/admin/module"] },
          { label: "System", href: "/admin/system" },
          { label: "Data", href: "/admin/data" },
        ],
      },
      {
        title: "Setup",
        items: [
          { label: "Categories", href: "/admin/categories" },
          { label: "Commitments", href: "/admin/commitments" },
          { label: "Custom Fields", href: "/admin/custom-fields" },
          { label: "Audit Log", href: "/admin/audit" },
        ],
      },
      {
        title: "Settings",
        items: [
          { label: "Preferences", href: "/preferences" },
          { label: "Security", href: "/security" },
          { label: "Recycle Bin", href: "/settings/recycle-bin" },
          { label: "Mail", href: "/settings/mail" },
        ],
      },
    ],
  },
];

function pathOf(href: string): string {
  return href.split("?")[0];
}

function queryOf(href: string): string {
  return href.includes("?") ? href.slice(href.indexOf("?")) : "";
}

function under(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + "/");
}

/** Modules this user may see, in bar order. */
export function visibleModules(
  visible: ReadonlySet<PageKey>,
  isAdmin: boolean,
): ModuleDef[] {
  return MODULES.filter(
    (m) => (!m.adminOnly || isAdmin) && (!m.pageKey || visible.has(m.pageKey)),
  ).map((m) => ({
    ...m,
    groups: m.groups
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (i) => (!i.adminOnly || isAdmin) && (!i.pageKey || visible.has(i.pageKey)),
        ),
      }))
      .filter((g) => g.items.length > 0),
  }));
}

/** The module owning `pathname`: the longest matching prefix wins. */
export function moduleFor(pathname: string, modules: ModuleDef[] = MODULES): ModuleDef | null {
  let best: { m: ModuleDef; len: number } | null = null;
  for (const m of modules) {
    for (const p of m.prefixes) {
      if (under(pathname, p) && (!best || p.length > best.len)) best = { m, len: p.length };
    }
  }
  return best?.m ?? null;
}

/**
 * The sidebar item for the current location. Longest match wins, an item with
 * a query string (Planned = `/budget?tab=planned`) wins only on that exact
 * query, and an `exact` item never lights for its sub-pages.
 */
export function activeItem(
  pathname: string,
  search: string,
  module: ModuleDef | null,
): ModuleItem | null {
  if (!module) return null;
  let best: { item: ModuleItem; score: number } | null = null;
  for (const g of module.groups) {
    for (const item of g.items) {
      const path = pathOf(item.href);
      const query = queryOf(item.href);
      let score = -1;
      if (query) {
        if (pathname === path && search === query) score = 10_000;
      } else if (item.exact) {
        if (pathname === path && !search.includes("tab=")) score = 5_000 + path.length;
        else if (pathname === path) score = path.length;
      } else {
        for (const p of [path, ...(item.alsoMatches ?? [])]) {
          if (under(pathname, p)) score = Math.max(score, p.length);
        }
      }
      if (score >= 0 && (!best || score > best.score)) best = { item, score };
    }
  }
  return best?.item ?? null;
}

export interface Crumb {
  label: string;
  href?: string;
}

/**
 * Module › section › (detail). The last crumb has no link. `detailLabel` is
 * what a detail page reports about itself (e.g. an account's name); without
 * one, a path deeper than the section reads "Details".
 */
export function breadcrumbFor(
  pathname: string,
  search: string,
  modules: ModuleDef[],
  detailLabel?: string | null,
): Crumb[] {
  const m = moduleFor(pathname, modules);
  if (!m) return [];
  const item = activeItem(pathname, search, m);
  const crumbs: Crumb[] = [{ label: m.label, href: m.href }];
  if (item) crumbs.push({ label: item.label, href: item.href });
  const itemPath = item ? pathOf(item.href) : m.href;
  const deeper = pathname !== itemPath && under(pathname, itemPath);
  if (detailLabel || deeper) crumbs.push({ label: detailLabel || "Details" });
  const last = crumbs[crumbs.length - 1];
  crumbs[crumbs.length - 1] = { label: last.label };
  return crumbs;
}
